<template>
  <div class="home">
    <!-- Willkommensbox für nicht eingeloggte Benutzer -->
    <div v-if="!isLoggedIn" class="info-box">
      <h1>Herzlich Willkommen bei Wetter-Wetter!</h1>
      <p>Wetter-Wetter ist ein Projekt für das POOSE Modul der Informatik der Uni-Bonn. Hier kann man mit Spielgeld auf Entwicklung von Temperatur und Sonnenstunden der nächsten Tage in Bonn wetten.</p>
      <p>Hannes, Christoph und Tilo wünschen <strong>Viel Spaß!</strong></p>
      <div style="width: 100%; height: 70px; "></div>
    </div>

    <h1>Laufende Wetten</h1>

    <!-- Ladezustand anzeigen -->
    <div v-if="loading" class="loading">
      Lade Wetten...
    </div>

    <!-- Fehlerzustand anzeigen -->
    <div v-else-if="error" class="error">
      {{ error }}
    </div>

    <!-- Wettkarten anzeigen, wenn Wetten vorhanden sind -->
    <div v-else-if="bets.length > 0" class="bets-container">
      <!-- Jede Wettkarte ist ein Link zur Detailseite der Wette -->
      <router-link
        v-for="bet in sortedBets"
        :key="bet.id"
        :to="{ name: 'WetteDetail', params: { id: bet.id } }"
        class="bet-card-link"
      >
        <article class="bet-card">
          <h2>{{ bet.title }}</h2>
          <p>{{ bet.description }}</p>

          <div class="bet-meta">
            <!-- Countdown anzeigen, wenn die Wette noch läuft -->
            <span v-if="bet.running" class="countdown">
              Ende in: {{ getCountdownText(bet.runningUntil) }}
            </span>
            <span v-else>Wettfenster beendet</span>

            <!-- Status der Wette anzeigen (abgerechnet oder ausstehend) -->
            <span v-if="bet.settled" class="settled">Ergebnis da</span>
            <span v-else-if="!bet.running" class="open">Ergebnis ausstehend</span>
          </div>
        </article>
      </router-link>
    </div>

    <!-- Nachricht anzeigen, wenn keine Wetten verfügbar sind -->
    <div v-else class="no-bets-message">
      <p>Aktuell sind keine Wetten verfügbar.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed  } from 'vue'
import { getRunningBets } from '@/services/api'
import { useBetCountdown } from '@/composables/useBetCountdown'
import { useAuth } from '@/composables/useAuth'

// Reaktive Zustandsvariablen für die Komponente
const bets = ref([])
const loading = ref(true)
const error = ref(null)

// Composables für Authentifizierung und Countdown
const { user } = useAuth()
const isLoggedIn = computed(() => !!user.value)

const { formatCountdown, getRemainingMs } = useBetCountdown()
let reloadTimer = null

/**
 * Lädt die laufenden Wetten vom Backend.
 */
const loadBets = async () => {
  try {
    const response = await getRunningBets()
    bets.value = response.answer
  } catch (err) {
    error.value = 'Fehler beim Laden der Wetten: ' + err.message
  } finally {
    loading.value = false
  }
}

/**
 * Formatiert den Countdown-Text für eine Wette.
 * @param {number} runningUntil - Der Endzeitstempel der Wette.
 * @returns {string} Der formatierte Countdown-String.
 */
const getCountdownText = (runningUntil) => {
  return formatCountdown(runningUntil)
}

/**
 * Eine berechnete Eigenschaft, die die Wetten sortiert.
 * - Laufende Wetten werden zuerst angezeigt.
 * - Laufende Wetten werden nach der kürzesten verbleibenden Zeit sortiert.
 * - Beendete Wetten werden nach dem jüngsten Enddatum sortiert.
 */
const sortedBets = computed(() => {
  return [...bets.value].sort((a, b) => {
    const aRemaining = getRemainingMs(a.runningUntil)
    const bRemaining = getRemainingMs(b.runningUntil)

    const aRunning = a.running && aRemaining > 0
    const bRunning = b.running && bRemaining > 0

    // Laufende Wetten zuerst
    if (aRunning && !bRunning) return -1
    if (!aRunning && bRunning) return 1

    // Beide laufen -> kürzeste Restlaufzeit zuerst
    if (aRunning && bRunning) {
      return aRemaining - bRemaining
    }

    // Beide beendet -> jüngstes Enddatum zuerst
    return new Date(b.runningUntil) - new Date(a.runningUntil)
  })
})

// Lifecycle-Hook: Wird ausgeführt, wenn die Komponente eingehängt wird.
onMounted(async () => {
  // Wetten beim Laden der Seite abrufen.
  await loadBets()

  // Ein Timer, der prüft, ob eine Wette abgelaufen ist, um die Liste neu zu laden.
  reloadTimer = setInterval(() => {
    if (bets.value.some((bet) => bet.running && getRemainingMs(bet.runningUntil) <= 0)) void loadBets()
  }, 200);
})

// Lifecycle-Hook: Wird ausgeführt, bevor die Komponente zerstört wird.
onBeforeUnmount(() => {
  // Den Timer aufräumen, um Speicherlecks zu vermeiden.
  if (reloadTimer) clearInterval(reloadTimer)
})
</script>

<style lang="scss" scoped>
.home {
  max-width: 80vw;
  margin: auto;
  padding: 20px;
}

.bets-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}

.bet-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.bet-card {
  background: $card-background;
  border: 1px solid $border-color;
  border-radius: 8px;
  padding: 16px;
  width: 300px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: left;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  display: flex;
  flex-direction: column;
  height: 100%;

  h2 {
    color: $secondary-color;
  }

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 14px rgba(0,0,0,0.12);
  }
}

.bet-meta {
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex-direction: column;
  font-size: 0.9rem;
  opacity: 0.85;
  margin-top: auto;
}

.countdown {
  font-family: monospace;
  font-weight: 700;
}

.settled {
  color: #198754;
  font-weight: 700;
}

.open {
  color: #6c757d;
}

.loading, .error, .no-bets-message {
  margin-top: 20px;
  font-size: 1.2em;
}

.error {
  color: red;
}
</style>